import { ValidationErrorType, type ValidationError } from '../types';

const VALIDATION_RULES = {
  PHONE_MIN_LENGTH: 10,
} as const;

const ERROR_MESSAGES = {
  PHONE_INCOMPLETE: 'Введите полный номер телефона',
  AGREEMENT_REQUIRED: 'Согласие обязательно',
  MORTGAGE_AMOUNT_REQUIRED: 'Введите остаток долга',
  MORTGAGE_RATE_REQUIRED: 'Введите ставку',
} as const;

/**
 * Валидирует номер телефона
 * @param phone - номер телефона для валидации
 * @param isSubmit - если true, показывает ошибки для пустых полей
 * @returns сообщение об ошибке или пустую строку если валидно
 */
export const validatePhone = (phone: string, isSubmit: boolean = false): string => {
  const cleanPhone = phone.replace(/\s/g, ''); // убираем пробелы

  if (cleanPhone.length === 0) {
    return isSubmit ? ERROR_MESSAGES.PHONE_INCOMPLETE : '';
  }

  if (cleanPhone.length < VALIDATION_RULES.PHONE_MIN_LENGTH) {
    return ERROR_MESSAGES.PHONE_INCOMPLETE;
  }

  return '';
};

/**
 * Валидирует согласие на получение рекламы
 * @param agreed - флаг согласия
 * @returns сообщение об ошибке или пустую строку если валидно
 */
export const validateAgreement = (agreed: boolean): string => {
  return agreed ? '' : ERROR_MESSAGES.AGREEMENT_REQUIRED;
};

/**
 * Валидирует остаток долга по ипотеке
 * @param mortgageAmountRemaining - остаток долга
 * @returns сообщение об ошибке или пустую строку если валидно
 */
export const validateMortgageAmount = (mortgageAmountRemaining?: number): string => {
  if (!mortgageAmountRemaining) {
    return ERROR_MESSAGES.MORTGAGE_AMOUNT_REQUIRED;
  }

  return '';
};

/**
 * Валидирует ставку по ипотеке
 * @param mortgageRate - ставка
 * @returns сообщение об ошибке или пустую строку если валидно
 */
export const validateMortgageRate = (mortgageRate?: number): string => {
  if (!mortgageRate) {
    return ERROR_MESSAGES.MORTGAGE_RATE_REQUIRED;
  }

  return '';
};

/**
 * Создает объект ошибки валидации
 * @param text - текст ошибки
 * @param type - тип ошибки
 * @returns объект ошибки валидации
 */
export const createValidationError = (text: string, type: ValidationErrorType): ValidationError => {
  return { text, type };
};

/**
 * Проверяет, является ли ошибка связанной с телефоном
 * @param error - ошибка валидации
 * @returns true если ошибка связана с телефоном
 */
export const isPhoneError = (error: ValidationError): boolean => {
  return error?.type === ValidationErrorType.Phone;
};

/**
 * Проверяет, является ли ошибка связанной с согласием
 * @param error - ошибка валидации
 * @returns true если ошибка связана с согласием
 */
export const isAgreementError = (error: ValidationError): boolean => {
  return error?.type === ValidationErrorType.Agreement;
};

/**
 * Проверяет, является ли ошибка связанной с остатком долга
 * @param error - ошибка валидации
 * @returns true если ошибка связана с остатком долга
 */
export const isMortgageAmountError = (error: ValidationError): boolean => {
  return error?.type === ValidationErrorType.MortgageAmount;
};

/**
 * Проверяет, является ли ошибка связанной со ставкой
 * @param error - ошибка валидации
 * @returns true если ошибка связана со ставкой
 */
export const isMortgageRateError = (error: ValidationError): boolean => {
  return error?.type === ValidationErrorType.MortgageRate;
};
